import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Button, Input } from '../components/ui';

// -----------------------------------------------------------------------------
// Invoice queue (admin)
// -----------------------------------------------------------------------------
// Lots whose Pour stage is complete and that have not been invoiced yet, from
// the get_ready_to_invoice() RPC. The office confirms the dollar amount (the
// number is not held anywhere automatically, it comes from the builder PO), and
// creates a DRAFT invoice in QuickBooks via the qbo-create-invoice function.
// The draft is not sent; it is reviewed and sent inside QuickBooks. Once a lot
// is invoiced it drops off the queue automatically (the RPC excludes it).
//
// The "Connect QuickBooks" button kicks off the one-time OAuth authorization
// (qbo-oauth-start mints a signed state and returns the Intuit authorize URL;
// we then send the browser there). On return, the URL carries ?qbo=connected
// or ?qbo=error&reason=..., which we surface as a banner and then clear.

interface ReadyRow {
    job_id: string;
    builder_name: string;
    qbo_customer_name: string | null;
    foundation_cost_code: string | null;
    community_name: string | null;
    lot_number: string | null;
    address: string | null;
    pour_date: string | null;
}

interface CreateVars {
    jobId: string;
    builder: string;
    costCode: string;
    amount: number;
    lotReference: string;
}

interface CreateResult {
    status: string;
    qbo_id?: string;
    doc_number?: string;
    total?: number;
}

const fieldLabel =
    'block font-mono text-[11px] uppercase tracking-widest text-graphite-600 dark:text-graphite-400 mb-1.5';

async function fetchReady(): Promise<ReadyRow[]> {
    const { data, error } = await supabase.rpc('get_ready_to_invoice');
    if (error) throw new Error(error.message);
    return (data ?? []) as ReadyRow[];
}

async function createDraftInvoice(vars: CreateVars): Promise<CreateResult> {
    const { data, error } = await supabase.functions.invoke('qbo-create-invoice', {
        body: {
            builder: vars.builder,
            cost_code: vars.costCode,
            amount: vars.amount,
            job_id: vars.jobId,
            request_id: vars.jobId, // one foundation invoice per job
            lot_reference: vars.lotReference,
        },
    });

    if (error) {
        let message = error.message;
        try {
            // supabase-js exposes the raw Response on error.context for non-2xx
            const ctx = (error as { context?: Response }).context;
            const body = ctx ? await ctx.json() : null;
            if (body?.status === 'in_progress') {
                message = 'This lot is already being invoiced. Refresh in a moment.';
            } else if (body?.message) {
                message = body.message;
            }
        } catch {
            /* fall back to error.message */
        }
        throw new Error(message);
    }

    return data as CreateResult;
}

export function InvoiceQueuePage() {
    const queryClient = useQueryClient();
    const { data: rows, isLoading, error } = useQuery({
        queryKey: ['ready_to_invoice'],
        queryFn: fetchReady,
    });
    const [lastResult, setLastResult] = useState<string | null>(null);

    // ---- QuickBooks connect ----
    const [connecting, setConnecting] = useState(false);
    const [connectError, setConnectError] = useState<string | null>(null);
    const [connectNotice, setConnectNotice] = useState<{ ok: boolean; text: string } | null>(null);

    // Read the ?qbo= result that Intuit's redirect left on the URL, then clear it
    // so it does not stick around on refresh.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const qbo = params.get('qbo');
        if (qbo === 'connected') {
            setConnectNotice({ ok: true, text: 'QuickBooks connected.' });
        } else if (qbo === 'error') {
            const reason = params.get('reason') ?? 'unknown';
            setConnectNotice({ ok: false, text: `QuickBooks connection failed (${reason}).` });
        }
        if (qbo) {
            params.delete('qbo');
            params.delete('reason');
            const qs = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
        }
    }, []);

    async function handleConnect() {
        setConnecting(true);
        setConnectError(null);
        try {
            const { data, error: invokeError } = await supabase.functions.invoke('qbo-oauth-start', {
                body: {},
            });
            if (invokeError) throw new Error(invokeError.message);
            const url = (data as { authorize_url?: string })?.authorize_url;
            if (!url) throw new Error('No authorization URL was returned.');
            window.location.href = url; // leave the app to authorize at Intuit
        } catch (e) {
            setConnectError((e as Error).message);
            setConnecting(false);
        }
    }

    const create = useMutation({
        mutationFn: createDraftInvoice,
        onSuccess: (res) => {
            setLastResult(
                res.status === 'already_created'
                    ? `A draft already existed for that lot (invoice ${res.qbo_id}).`
                    : `Draft invoice ${res.doc_number ?? res.qbo_id} created in QuickBooks.`
            );
            void queryClient.invalidateQueries({ queryKey: ['ready_to_invoice'] });
        },
    });

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
            <div className="mb-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <h1 className="font-display font-bold text-2xl text-graphite-900 dark:text-graphite-50">
                        Ready to invoice
                    </h1>
                    <p className="text-sm text-graphite-600 dark:text-graphite-400 mt-1">
                        Lots whose pour is complete and that have not been invoiced. Confirm the
                        amount from the builder PO, then create a draft. Drafts are not sent, you
                        review and send them in QuickBooks.
                    </p>
                </div>
                <div className="shrink-0">
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={connecting}
                        onClick={handleConnect}
                    >
                        {connecting ? 'Connecting...' : 'Connect QuickBooks'}
                    </Button>
                </div>
            </div>

            {connectNotice ? (
                <div
                    className={
                        connectNotice.ok
                            ? 'mb-4 border border-done-500/30 bg-done-500/[0.06] text-graphite-800 dark:text-graphite-200 px-4 py-2.5 rounded font-mono text-xs'
                            : 'mb-4 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-4 py-2.5 rounded font-mono text-xs'
                    }
                >
                    {connectNotice.text}
                </div>
            ) : null}

            {connectError ? (
                <div className="mb-4 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-4 py-2.5 rounded font-mono text-xs">
                    Could not start the QuickBooks connection. {connectError}
                </div>
            ) : null}

            {lastResult ? (
                <div className="mb-4 border border-done-500/30 bg-done-500/[0.06] text-graphite-800 dark:text-graphite-200 px-4 py-2.5 rounded font-mono text-xs">
                    {lastResult}
                </div>
            ) : null}

            {create.error ? (
                <div className="mb-4 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-4 py-2.5 rounded font-mono text-xs">
                    {create.error.message}
                </div>
            ) : null}

            {isLoading ? (
                <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                    Loading...
                </div>
            ) : error ? (
                <div className="border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-4 py-3 rounded text-sm">
                    Could not load the queue. {(error as Error).message}
                </div>
            ) : !rows || rows.length === 0 ? (
                <div className="border border-graphite-200 dark:border-graphite-800 rounded px-5 py-10 text-center text-sm text-graphite-500">
                    Nothing waiting to be invoiced right now.
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((row) => (
                        <InvoiceRow
                            key={row.job_id}
                            row={row}
                            submitting={create.isPending && create.variables?.jobId === row.job_id}
                            onCreate={(amount, costCode) =>
                                create.mutate({
                                    jobId: row.job_id,
                                    builder: row.qbo_customer_name ?? row.builder_name,
                                    costCode,
                                    amount,
                                    lotReference: [row.community_name, row.lot_number]
                                        .filter(Boolean)
                                        .join(' '),
                                })
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function InvoiceRow({
    row,
    submitting,
    onCreate,
}: {
    row: ReadyRow;
    submitting: boolean;
    onCreate: (amount: number, costCode: string) => void;
}) {
    const [amount, setAmount] = useState('');
    const [costCode, setCostCode] = useState(row.foundation_cost_code ?? '');

    const amountNum = Number(amount);
    const canSubmit = costCode.trim().length > 0 && amountNum > 0 && !submitting;

    return (
        <div className="border border-graphite-200 dark:border-graphite-800 rounded p-4 flex flex-col md:flex-row md:items-end gap-4">
            <div className="min-w-0 flex-1">
                <div className="font-sans font-semibold text-graphite-900 dark:text-graphite-100">
                    {row.builder_name}
                    {row.community_name ? ` · ${row.community_name}` : ''}
                    {row.lot_number ? ` · Lot ${row.lot_number}` : ''}
                </div>
                <div className="text-sm text-graphite-500 mt-0.5 truncate">
                    {row.address ?? '—'}
                    {row.pour_date ? ` · poured ${row.pour_date}` : ''}
                </div>
            </div>

            <div className="w-full md:w-36">
                <span className={fieldLabel}>Cost code</span>
                <Input
                    id={`cc-${row.job_id}`}
                    value={costCode}
                    onChange={(e) => setCostCode(e.target.value)}
                    placeholder="Cost code"
                />
            </div>

            <div className="w-full md:w-36">
                <span className={fieldLabel}>Amount</span>
                <Input
                    id={`amt-${row.job_id}`}
                    type="number"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                />
            </div>

            <div>
                <Button
                    variant="primary"
                    size="sm"
                    disabled={!canSubmit}
                    onClick={() => onCreate(amountNum, costCode.trim())}
                >
                    {submitting ? 'Creating...' : 'Create draft'}
                </Button>
            </div>
        </div>
    );
}
